import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { set } from '@foleyjs/core';
import {
  FOLEY_SETTINGS,
  bindCueDelegation,
  getStoredSoundEnabled,
  primeAudioOnFirstGesture,
  setMuted,
  storeSoundEnabled,
} from '../lib/foley';

interface SoundContextType {
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

interface SoundProviderProps {
  children: ReactNode;
}

/**
 * Owns the Foley engine: applies our settings, delegates the `data-foley-*`
 * attributes, and exposes the player's mute preference. Mount it once, above
 * the whole app.
 */
export const SoundProvider: React.FC<SoundProviderProps> = ({ children }) => {
  const [soundEnabled, setSoundEnabledState] = useState(getStoredSoundEnabled);

  useEffect(() => {
    set(FOLEY_SETTINGS);
    // Apply the stored preference before any cue can fire.
    setMuted(!getStoredSoundEnabled());

    const unbindCues = bindCueDelegation();
    const unbindPrime = primeAudioOnFirstGesture();

    return () => {
      unbindCues();
      unbindPrime();
    };
  }, []);

  const setSoundEnabled = useCallback((enabled: boolean) => {
    // Mute synchronously so a caller can play a confirmation cue immediately
    // after switching sound back on.
    setMuted(!enabled);
    storeSoundEnabled(enabled);
    setSoundEnabledState(enabled);
  }, []);

  const contextValue = useMemo(
    () => ({ soundEnabled, setSoundEnabled }),
    [soundEnabled, setSoundEnabled],
  );

  return <SoundContext.Provider value={contextValue}>{children}</SoundContext.Provider>;
};

export const useSound = (): SoundContextType => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};
